import {chunk} from '@benchling/js-utils';
import React from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {RESTIdentifiedWarehouseParameter} from 'analysis-tool/data-frame-creation/DataFrameCreationTypes';
import {DataViewContextValue} from 'analysis-tool/data-view/DataViewContext';
import {ReadOnlyDataFrameColumnRelationshipMultiSelector} from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipMultiSelector';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {JOIN_TYPE_TO_TITLE} from 'pipelines/joins/JoinConstants';
import {PipelineStep, pipelineStepIsStepType} from 'pipelines/PipelineTypes';
import {PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getInputStepDataRawDatas} from 'pipelines/StepDataUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import EntryChip from 'ui-library/chips/EntryChip';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {JSONValue} from 'util/Types';

/**
 * User-facing terms for how a table was created.
 *
 * These are shown under "Created from" in a metadata modal.
 *
 * This overrides `STEP_TYPE_TO_USER_FACING_NAME` because certain names read awkwardly under "Created from",
 * e.g. "Created from: Create table from Insights block"
 */
const STEP_TYPE_TO_CREATION_METHOD_SUMMARY: {[StepType in PipelineStepType]: string} = {
  ...STEP_TYPE_TO_USER_FACING_NAME,
  [PipelineStepType.CONNECTION_DATA_DATASET]: 'Connection data',
  [PipelineStepType.DATA_CATALOG_DATASET]: 'Dataset',
  [PipelineStepType.DATA_CATALOG_FILE]: 'File',
  [PipelineStepType.DATA_CATALOG_FILES]: 'Files',
  [PipelineStepType.INITIALIZE_DATASET]: 'Insights block',
  [PipelineStepType.NOTEBOOK_TABLE_DATASET]: 'Notebook table',
  [PipelineStepType.PLATE_SCHEMA_DATASET]: 'Plate schema',
  [PipelineStepType.PROCEDURE_RUN_DATA_FRAMES]: 'Experiment run data',
  [PipelineStepType.REGISTRY_SCHEMA_DATASET]: 'Registry schema',
  [PipelineStepType.THIRD_PARTY]: 'API',
  [PipelineStepType.UPLOAD_CSV_DATASET]: 'Upload CSV',
};

function stepToAdditionalGridCells(step: PipelineStep): Array<{component: React.ReactNode; label: string}> {
  if (step.config == null) {
    return [];
  }

  if (pipelineStepIsStepType(PipelineStepType.INITIALIZE_DATASET, 2, step)) {
    return [
      {
        component: step.config.dashboardName,
        label: 'Dashboard name',
      },
      {
        component: step.config.blockName,
        label: 'Block name',
      },
    ];
  } else if (pipelineStepIsStepType(PipelineStepType.NOTEBOOK_TABLE_DATASET, 0, step)) {
    return [
      {
        component: <EntryChip id={step.config.entryId} size={ChipSize.MINI} />,
        label: 'Entry',
      },
      {
        component: step.config.tableName,
        label: 'Table name',
      },
    ];
  } else if (pipelineStepIsStepType(PipelineStepType.JOIN, 0, step)) {
    return [
      {
        component: JOIN_TYPE_TO_TITLE[step.config.joinType],
        label: 'Join type',
      },
      {
        component: getInputStepDataRawDatas(step, 'Left')[0].name,
        label: 'Left',
      },
      {
        component: getInputStepDataRawDatas(step, 'Right')[0].name,
        label: 'Right',
      },
    ];
  } else if (pipelineStepIsStepType(PipelineStepType.DATA_CATALOG_DATASET, 0, step)) {
    return [
      {
        component: <BenchChip id={step.config.datasetId} size={ChipSize.MINI} />,
        label: 'Dataset',
      },
    ];
  } else if (pipelineStepIsStepType(PipelineStepType.DATA_CATALOG_FILE, 0, step)) {
    return [
      {
        component: <BenchChip id={step.config.fileId} size={ChipSize.MINI} />,
        label: 'File',
      },
    ];
  } else if (pipelineStepIsStepType(PipelineStepType.DATA_CATALOG_FILES, 0, step)) {
    return step.config.fileIds.map((fileId) => ({
      component: <BenchChip id={fileId} size={ChipSize.MINI} />,
      label: 'File',
    }));
  }

  return [];
}

function ParameterCol({
  parameterValue,
  parameterConfig,
}: {
  parameterValue: JSONValue;
  parameterConfig: Omit<RESTIdentifiedWarehouseParameter['config'], 'type'>;
}): JSX.Element {
  return (
    <Col sm={6} dataTestAttrs={{component: 'ParameterCol', key: parameterConfig.systemName}}>
      <FormGroup key={parameterConfig.id}>
        <FieldLabel label={`{{${parameterConfig.systemName}}}`} />
        <div
          {...getTestProps({
            component: 'ViewMetadataModal',
            element: 'value',
            key: parameterConfig.systemName,
          })}
        >
          {parameterValue}
        </div>
      </FormGroup>
    </Col>
  );
}

function AdditionalMetadataModalContent({step}: {step: PipelineStep}): JSX.Element | null {
  if (step.config == null) {
    return null;
  }

  if (pipelineStepIsStepType(PipelineStepType.INITIALIZE_DATASET, 2, step)) {
    return (
      <>
        {[...chunk(step.config.parameters, 2)].map(([parameter1, parameter2], index) => (
          <Row key={index}>
            <ParameterCol parameterConfig={parameter1.config} parameterValue={parameter1.valueJson} />
            {parameter2 != null && (
              <ParameterCol parameterConfig={parameter2.config} parameterValue={parameter2.valueJson} />
            )}
          </Row>
        ))}
        <pre {...getTestProps({component: 'AdditionalMetadataModalContent', key: 'parameterizedSqlQuery'})}>
          {step.config.parameterizedSQLQuery}
        </pre>
      </>
    );
  } else if (pipelineStepIsStepType(PipelineStepType.REGISTRY_SCHEMA_DATASET, 0, step)) {
    return (
      <pre {...getTestProps({component: 'AdditionalMetadataModalContent', key: 'queryDescription'})}>
        {step.config.queryDescription}
      </pre>
    );
  } else if (pipelineStepIsStepType(PipelineStepType.JOIN, 0, step)) {
    // There is no column mapping for positional joins (aka 'append')
    if (step.config.joinType === 'POSITIONAL') {
      return null;
    }

    return (
      <ReadOnlyDataFrameColumnRelationshipMultiSelector
        leftDataFrame={getInputStepDataRawDatas(step, 'Left')[0]}
        rightDataFrame={getInputStepDataRawDatas(step, 'Right')[0]}
        relationships={step.config.joinConditions.map(({leftDataSourceColumn, rightDataSourceColumn}) => ({
          leftColumn: leftDataSourceColumn,
          rightColumn: rightDataSourceColumn,
        }))}
      />
    );
  }
  return null;
}

/**
 * Get step-specific content for the metadata modal from a step.
 *
 */
export function getStepMetadataContent(step: PipelineStep): DataViewContextValue['metadataContent'] {
  return {
    gridCells: [
      {
        component: STEP_TYPE_TO_CREATION_METHOD_SUMMARY[step.stepType],
        label: 'Created from',
      },
      ...stepToAdditionalGridCells(step),
    ],
    additionalContent: <AdditionalMetadataModalContent step={step} />,
  };
}

/**
 * Get view-specific content for the metadata modal
 */
export default function getAnalysisViewMetadataContent(
  analysis: AnalysisGraphQL,
  view: PipelineViewGraphQL
): DataViewContextValue['metadataContent'] {
  const step = analysis.steps.find(({id}) => id === view.lineage[0].step.id)!;
  return getStepMetadataContent(step);
}
